/**
 * CompleteProfileModal Component
 * Modal for completing user profile after Google OAuth registration
 * Collects phone number (required) and optional address information
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Phone, MapPin, Calendar } from 'lucide-react';
import { handleBirthdayInput, formatBirthdayForBackend, validateBirthday } from '../utils/brazilianUtils';

// Brazilian states for dropdown
const BRAZILIAN_STATES = [
  { code: 'AC', name: 'Acre' },
  { code: 'AL', name: 'Alagoas' },
  { code: 'AP', name: 'Amapá' },
  { code: 'AM', name: 'Amazonas' },
  { code: 'BA', name: 'Bahia' },
  { code: 'CE', name: 'Ceará' },
  { code: 'DF', name: 'Distrito Federal' },
  { code: 'ES', name: 'Espírito Santo' },
  { code: 'GO', name: 'Goiás' },
  { code: 'MA', name: 'Maranhão' },
  { code: 'MT', name: 'Mato Grosso' },
  { code: 'MS', name: 'Mato Grosso do Sul' },
  { code: 'MG', name: 'Minas Gerais' },
  { code: 'PA', name: 'Pará' },
  { code: 'PB', name: 'Paraíba' },
  { code: 'PR', name: 'Paraná' },
  { code: 'PE', name: 'Pernambuco' },
  { code: 'PI', name: 'Piauí' },
  { code: 'RJ', name: 'Rio de Janeiro' },
  { code: 'RN', name: 'Rio Grande do Norte' },
  { code: 'RS', name: 'Rio Grande do Sul' },
  { code: 'RO', name: 'Rondônia' },
  { code: 'RR', name: 'Roraima' },
  { code: 'SC', name: 'Santa Catarina' },
  { code: 'SP', name: 'São Paulo' },
  { code: 'SE', name: 'Sergipe' },
  { code: 'TO', name: 'Tocantins' }
];

// Format CEP
const formatCep = (cep) => {
  if (!cep) return '';
  const numbers = cep.replace(/\D/g, '');
  if (numbers.length <= 8) {
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  }
  return numbers.slice(0, 8).replace(/(\d{5})(\d{3})/, '$1-$2');
};

// Format phone number
const formatPhone = (phone) => {
  if (!phone) return '';
  const numbers = phone.replace(/\D/g, '');
  if (numbers.length <= 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

// CEP lookup via ViaCEP API
const lookupCep = async (cep) => {
  const cleanCep = cep.replace(/\D/g, '');
  if (cleanCep.length !== 8) return null;

  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
    const data = await response.json();

    if (data.erro) return null;

    return {
      address_street: data.logradouro || '',
      address_neighborhood: data.bairro || '',
      address_city: data.localidade || '',
      address_state: data.uf || '',
      address_complement: data.complemento || ''
    };
  } catch (error) {
    return null;
  }
};

const CompleteProfileModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    phone_number: '',
    birthday: '',
    address_cep: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_neighborhood: '',
    address_city: '',
    address_state: ''
  });
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [error, setError] = useState('');

  const phoneInputRef = useRef(null);

  // Focus phone input when modal opens
  useEffect(() => {
    if (isOpen && phoneInputRef.current) {
      setTimeout(() => {
        phoneInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        phone_number: '',
        birthday: '',
        address_cep: '',
        address_street: '',
        address_number: '',
        address_complement: '',
        address_neighborhood: '',
        address_city: '',
        address_state: ''
      });
      setShowAddressFields(false);
      setError('');
    }
  }, [isOpen]);

  // Handle CEP lookup
  const handleCepLookup = async (cep) => {
    const addressData = await lookupCep(cep);
    if (addressData) {
      setFormData(prev => ({
        ...prev,
        address_street: addressData.address_street || prev.address_street,
        address_neighborhood: addressData.address_neighborhood || prev.address_neighborhood,
        address_city: addressData.address_city || prev.address_city,
        address_state: addressData.address_state || prev.address_state,
        address_complement: addressData.address_complement || prev.address_complement
      }));
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate phone number
    const cleanPhone = formData.phone_number.replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length < 10) {
      setError('Por favor, informe um numero de telefone valido.');
      return;
    }

    // Validate birthday if provided
    if (formData.birthday) {
      const birthdayValidation = validateBirthday(formData.birthday);
      if (!birthdayValidation.valid) {
        setError(birthdayValidation.error);
        return;
      }
    }

    setError('');

    // Convert birthday to backend format before submitting
    const submitData = {
      ...formData,
      date_of_birth: formatBirthdayForBackend(formData.birthday)
    };
    delete submitData.birthday; // Remove the frontend-only birthday field

    onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-brand-light dark:bg-brand-dark w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-brand-dark/10 dark:border-brand-light/10 bg-brand-dark dark:bg-brand-light">
          <div>
            <h2 className="text-lg font-serif font-bold text-brand-light dark:text-brand-dark tracking-wide">
              Complete seu Perfil
            </h2>
            <p className="text-xs text-brand-light/70 dark:text-brand-dark/70 tracking-wider uppercase">
              Apenas mais um passo
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 border border-brand-light/20 dark:border-brand-dark/20 hover:bg-brand-light/10 dark:hover:bg-brand-dark/10 transition-all duration-300"
            aria-label="Fechar modal"
          >
            <X size={20} className="text-brand-light dark:text-brand-dark" strokeWidth={1.5} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Welcome message */}
            <div className="text-center mb-6">
              <p className="text-brand-dark/80 dark:text-brand-light/80 text-sm tracking-wide">
                Bem-vindo! Para continuar, precisamos do seu número de telefone para contato.
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-4 border border-red-600/20 dark:border-red-400/20 bg-transparent">
                <p className="text-red-600 dark:text-red-400 text-sm text-center tracking-wide">{error}</p>
              </div>
            )}

            {/* Phone Number (Required) */}
            <div>
              <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                <Phone size={14} className="inline mr-2" strokeWidth={1.5} />
                Telefone / WhatsApp *
              </label>
              <input
                ref={phoneInputRef}
                type="tel"
                value={formData.phone_number}
                onChange={(e) => setFormData(prev => ({ ...prev, phone_number: formatPhone(e.target.value) }))}
                className="w-full px-4 py-4 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                placeholder="(11) 99999-9999"
                autoComplete="tel"
                inputMode="tel"
                maxLength={15}
                required
              />
            </div>

            {/* Birthday (Optional) */}
            <div>
              <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                <Calendar size={14} className="inline mr-2" strokeWidth={1.5} />
                Aniversario (Dia/Mes)
              </label>
              <input
                type="text"
                value={formData.birthday}
                onChange={(e) => setFormData(prev => ({ ...prev, birthday: handleBirthdayInput(e.target.value) }))}
                className="w-full px-4 py-4 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                placeholder="DD/MM (ex: 15/03)"
                maxLength={5}
                inputMode="numeric"
              />
            </div>

            {/* Address Toggle */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowAddressFields(!showAddressFields)}
                className="text-brand-dark dark:text-brand-light font-medium text-xs py-2 px-4 border border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300 uppercase tracking-wider"
              >
                <MapPin size={14} className="inline mr-2" strokeWidth={1.5} />
                {showAddressFields ? 'Ocultar endereço' : 'Adicionar endereço (opcional)'}
                <span className="ml-1">{showAddressFields ? '▲' : '▼'}</span>
              </button>
            </div>

            {/* Address Fields (Optional) */}
            {showAddressFields && (
              <div className="space-y-4 pt-4 border-t border-brand-dark/10 dark:border-brand-light/10">
                {/* CEP */}
                <div>
                  <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                    CEP
                  </label>
                  <input
                    type="text"
                    value={formData.address_cep}
                    onChange={(e) => setFormData(prev => ({ ...prev, address_cep: formatCep(e.target.value) }))}
                    onBlur={(e) => {
                      if (e.target.value.replace(/\D/g, '').length === 8) {
                        handleCepLookup(e.target.value);
                      }
                    }}
                    className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                    placeholder="00000-000"
                    maxLength={9}
                    inputMode="numeric"
                  />
                  <p className="text-xs text-brand-dark/50 dark:text-brand-light/50 mt-1 tracking-wide">
                    O endereço será preenchido automaticamente
                  </p>
                </div>

                {/* Street and Number */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                      Rua
                    </label>
                    <input
                      type="text"
                      value={formData.address_street}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_street: e.target.value }))}
                      className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                      placeholder="Nome da rua"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                      Nº
                    </label>
                    <input
                      type="text"
                      value={formData.address_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_number: e.target.value }))}
                      className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                      placeholder="123"
                      inputMode="numeric"
                    />
                  </div>
                </div>

                {/* Complement and Neighborhood */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                      Complemento
                    </label>
                    <input
                      type="text"
                      value={formData.address_complement}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_complement: e.target.value }))}
                      className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                      placeholder="Apto, Bloco"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                      Bairro
                    </label>
                    <input
                      type="text"
                      value={formData.address_neighborhood}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_neighborhood: e.target.value }))}
                      className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                      placeholder="Bairro"
                    />
                  </div>
                </div>

                {/* City and State */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                      Cidade
                    </label>
                    <input
                      type="text"
                      value={formData.address_city}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_city: e.target.value }))}
                      className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                      placeholder="Cidade"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                      UF
                    </label>
                    <select
                      value={formData.address_state}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_state: e.target.value }))}
                      className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-brand-light dark:bg-brand-dark text-brand-dark dark:text-brand-light focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                    >
                      <option value="">UF</option>
                      {BRAZILIAN_STATES.map(state => (
                        <option key={state.code} value={state.code}>
                          {state.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="border-t border-brand-dark/10 dark:border-brand-light/10 p-4">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 text-brand-dark dark:text-brand-light font-medium border border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300 uppercase tracking-wider text-xs"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || !formData.phone_number}
              className="flex-1 py-3 px-4 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark font-medium border border-brand-dark dark:border-brand-light hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 disabled:bg-brand-dark/30 dark:disabled:bg-brand-light/30 disabled:cursor-not-allowed transition-all duration-300 uppercase tracking-widest text-xs flex items-center justify-center"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-brand-light dark:border-brand-dark border-t-transparent animate-spin"></div>
              ) : (
                'Continuar'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfileModal;
